import { describeSendTracking, type DocumentSendLike } from './document-sends.js'
import type { JobStageKey } from './job-stages.js'

/**
 * What the compose window's Assistant offers before anybody types.
 *
 * Rules, not a model: the panel has to be there the instant the window opens,
 * on every Email button, and cost nothing. The model is asked for words only
 * when somebody ticks a suggestion, changes the tone or types an instruction.
 *
 * Each suggestion carries its own `brief` — the task in plain words, built
 * from the job's facts — and the server re-derives the list from the database
 * before drafting, so a client can pick a suggestion by key but never supply
 * what it says. Decision 95.
 */

export type ComposeChannel = 'EMAIL' | 'SMS'

export const COMPOSE_TONES = [
  { key: 'FORMAL', label: 'Formal' },
  { key: 'FRIENDLY', label: 'Friendly' },
  { key: 'BRIEF', label: 'Brief' },
] as const

export type ComposeTone = (typeof COMPOSE_TONES)[number]['key']

export const COMPOSE_TONE_KEYS = COMPOSE_TONES.map((tone) => tone.key) as [ComposeTone, ...ComposeTone[]]

/** What the highlight toolbar and the rewrite endpoint can do to some words. */
export const COMPOSE_REWRITE_ACTIONS = [
  'rewrite',
  'shorter',
  'simpler',
  'friendlier',
  'formal',
  'remove_price',
  'fix',
  'custom',
] as const

export type ComposeRewriteAction = (typeof COMPOSE_REWRITE_ACTIONS)[number]

/** A dollar figure, which is when the toolbar offers Remove price. */
export const PRICE_PATTERN = /\$\s?\d/

export type ComposeReferenceKind = 'quote' | 'invoice' | 'email' | 'sms' | 'note' | 'booking' | 'job' | 'report'

export interface ComposeReference {
  /** `kind:id`, so the server can tell which table to check it against. */
  id: string
  kind: ComposeReferenceKind
  label: string
  detail: string | null
}

export type ComposeIntentKind =
  | 'reply'
  | 'quote_follow_up'
  | 'quote_expiry'
  | 'offer_time'
  | 'booking_confirm'
  | 'booking_reminder'
  | 'payment_reminder'
  | 'send_report'
  | 'cover_note'

export interface ComposeSuggestion {
  /** Stable for one job's state, and what the client sends back. */
  key: string
  kind: ComposeIntentKind
  title: string
  detail: string
  /** What the model is asked to do, in plain words. Server side it is re-derived. */
  brief: string
  referenceIds: string[]
  /** Files the draft should carry. Always empty for SMS. */
  attachmentIds: string[]
}

export interface ComposeSignal {
  headline: string
  /** The provider's and the page's report, in `describeSendTracking`'s words. */
  tracking: string | null
  tone: 'waiting' | 'problem' | 'info'
}

export interface ComposeQuoteFacts {
  id: string
  number: string
  status: string
  isProposal: boolean
  accepted: boolean
  totalIncTax: number
  sentAt: Date | null
  validUntil: Date | null
  /** The newest send, with its tracking. */
  lastSend: DocumentSendLike | null
  sendCount: number
}

export interface ComposeInvoiceFacts {
  id: string
  number: string
  status: string
  totalIncTax: number
  amountPaid: number
  dueAt: Date | null
  sentAt: Date | null
  lastSend: DocumentSendLike | null
}

export interface ComposeVisitFacts {
  id: string
  startAt: Date
  endAt: Date
  status: string
  crew: string[]
}

export interface ComposeMessageFacts {
  id: string
  channel: ComposeChannel
  direction: 'INBOUND' | 'OUTBOUND'
  at: Date
  fromName: string | null
  subject: string | null
  preview: string | null
}

export interface ComposeJobFacts {
  stage: JobStageKey | null
  quotes: ComposeQuoteFacts[]
  invoices: ComposeInvoiceFacts[]
  visits: ComposeVisitFacts[]
  /** Newest first. */
  messages: ComposeMessageFacts[]
  notes: Array<{ id: string; at: Date; author: string | null; body: string }>
  reports: Array<{ attachmentId: string; name: string; at: Date }>
  pastJobs: Array<{ id: string; label: string; at: Date }>
  /** A time somebody could go, when the server could find one. */
  freeSlot: { userName: string; start: Date; place: string | null } | null
}

export interface ComposeSuggestionInput {
  job: ComposeJobFacts | null
  channel: ComposeChannel
  now: Date
  timeZone: string
  /** The recipient's first name, for "Reply to Margaret's email". */
  recipientFirstName: string | null
  /** A quote or invoice send window. */
  document: { kind: 'quote' | 'invoice'; id: string } | null
  /** A reply window: the message being answered. */
  replyTo: ComposeMessageFacts | null
}

export interface ComposeSuggestions {
  signal: ComposeSignal | null
  suggestions: ComposeSuggestion[]
  references: ComposeReference[]
}

/** How long after a quote goes out before a follow-up is worth sending. */
export const FOLLOW_UP_AFTER_DAYS = 3
/** How close to its expiry a quote's "expires soon" appears. */
export const EXPIRY_WARNING_DAYS = 7
/** How far ahead a booking is worth confirming or reminding about. */
export const BOOKING_AHEAD_DAYS = 7

const DAY = 24 * 60 * 60 * 1000
const OPEN_QUOTE = new Set(['SENT', 'VIEWED'])
const OWED_INVOICE = new Set(['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'])
const LIVE_VISIT = new Set(['PROPOSED', 'CONFIRMED', 'EN_ROUTE'])

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function composeDay(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-AU', { timeZone, weekday: 'short', day: 'numeric', month: 'short' })
    .format(value)
    .replace(',', '')
}

export function composeTime(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-AU', { timeZone, hour: 'numeric', minute: '2-digit' })
    .format(value)
    .replace(/\s/g, '')
    .toLowerCase()
}

function daysBetween(from: Date, to: Date): number {
  return Math.max(0, Math.floor((to.getTime() - from.getTime()) / DAY))
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

function possessive(name: string | null): string {
  return name ? `${name}’s` : 'the customer’s'
}

/** A send's report, shortened for one line in the panel. */
function trackingOf(send: DocumentSendLike | null, timeZone: string): { text: string; problem: boolean } | null {
  if (!send) return null
  return describeSendTracking(send, (value) => composeDay(value, timeZone))
}

function lastInbound(messages: ComposeMessageFacts[]): ComposeMessageFacts | null {
  const newest = messages[0]
  return newest && newest.direction === 'INBOUND' ? newest : null
}

function inboundSince(messages: ComposeMessageFacts[], since: Date): boolean {
  return messages.some((message) => message.direction === 'INBOUND' && message.at > since)
}

function messageLabel(message: ComposeMessageFacts, timeZone: string): string {
  return `${message.channel === 'SMS' ? 'SMS' : 'Email'} ${composeDay(message.at, timeZone)}`
}

/**
 * Everything the panel draws before anybody types: one signal line, the
 * suggestions in the order they matter, and what could be referenced.
 */
export function composeSuggestions(input: ComposeSuggestionInput): ComposeSuggestions {
  const { job, channel, now, timeZone } = input
  const email = channel === 'EMAIL'
  const suggestions: ComposeSuggestion[] = []
  const add = (suggestion: ComposeSuggestion) => {
    if (!suggestions.some((existing) => existing.key === suggestion.key)) suggestions.push(suggestion)
  }

  const replyTarget = input.replyTo ?? (job ? lastInbound(job.messages) : null)
  const answering = input.replyTo !== null

  if (input.document && job) {
    const quote = input.document.kind === 'quote' ? job.quotes.find((row) => row.id === input.document?.id) : null
    const invoice = input.document.kind === 'invoice' ? job.invoices.find((row) => row.id === input.document?.id) : null
    const number = quote?.number ?? invoice?.number ?? ''
    const what = quote ? (quote.isProposal ? 'proposal' : 'quote') : 'invoice'
    add({
      key: `cover_note:${input.document.kind}:${input.document.id}`,
      kind: 'cover_note',
      title: `Cover note for the ${what}`,
      detail: `A short note above ${number || `the ${what}`}. The PDF goes either way.`,
      brief: [
        `Write a short cover note for ${what} ${number}, which is attached to this email as a PDF${
          quote ? ' with a link to view and accept it online' : ' with a link to view it online'
        }.`,
        quote ? `Its total is ${money(quote.totalIncTax)} inc GST.` : null,
        invoice ? `Amount due: ${money(invoice.totalIncTax - invoice.amountPaid)} inc GST${invoice.dueAt ? `, due ${composeDay(invoice.dueAt, timeZone)}` : ''}.` : null,
        (quote?.sendCount ?? 0) > 0 ? 'It has been sent before; this is an updated copy, so say so plainly.' : null,
      ]
        .filter(Boolean)
        .join(' '),
      referenceIds: quote ? [`quote:${quote.id}`] : invoice ? [`invoice:${invoice.id}`] : [],
      attachmentIds: [],
    })
  }

  if (replyTarget) {
    const first = input.recipientFirstName ?? replyTarget.fromName?.split(/\s+/)[0] ?? null
    const via = replyTarget.channel === 'SMS' ? 'SMS' : 'email'
    add({
      key: `reply:${replyTarget.id}`,
      kind: 'reply',
      title: replyTarget.subject && replyTarget.channel === 'EMAIL'
        ? `Reply to “${replyTarget.subject.replace(/^(re|fwd?):\s*/i, '').slice(0, 60)}”`
        : `Answer ${possessive(first)} ${via}`,
      detail: `From ${first ? `${first}’s` : 'their'} ${via} on ${composeDay(replyTarget.at, timeZone)}${
        answering ? '' : ', not answered yet'
      }`,
      brief: `Answer the customer's ${via} of ${composeDay(replyTarget.at, timeZone)} (referenced below). Address each question they asked, using only facts from the job. If something they asked cannot be answered from the job, say the office will confirm it.`,
      referenceIds: [`${replyTarget.channel === 'SMS' ? 'sms' : 'email'}:${replyTarget.id}`],
      attachmentIds: [],
    })
  }

  if (job) {
    for (const quote of job.quotes) {
      if (!OPEN_QUOTE.has(quote.status) || quote.accepted || !quote.sentAt) continue
      const lastSentAt = quote.lastSend?.sentAt ?? quote.sentAt
      const waited = daysBetween(lastSentAt, now)
      const what = quote.isProposal ? 'proposal' : 'quote'
      const pdf = email && quote.lastSend?.attachment ? [quote.lastSend.attachment.id] : []

      if (job.stage === 'QUOTE' && waited >= FOLLOW_UP_AFTER_DAYS) {
        const heardSince = inboundSince(job.messages, lastSentAt)
        add({
          key: `quote_follow_up:${quote.id}`,
          kind: 'quote_follow_up',
          title: `${quote.isProposal ? 'Proposal' : 'Quote'} follow-up`,
          detail: pdf.length ? `Checks in and keeps the ${what} PDF attached` : `Checks in on ${what} ${quote.number}`,
          brief: `Follow up ${what} ${quote.number} (${money(quote.totalIncTax)} inc GST), sent ${composeDay(
            lastSentAt,
            timeZone,
          )}, ${plural(waited, 'day')} ago${
            heardSince ? '. The customer has been in touch since but has not accepted it' : ' with no reply'
          }. Check in politely, offer to answer questions, and make it easy to go ahead. Do not pressure.${
            pdf.length ? ' The same PDF is attached again; mention it.' : ''
          }`,
          referenceIds: [`quote:${quote.id}`],
          attachmentIds: pdf,
        })
      }

      if (quote.validUntil && quote.validUntil > now && daysBetween(now, quote.validUntil) <= EXPIRY_WARNING_DAYS) {
        add({
          key: `quote_expiry:${quote.id}`,
          kind: 'quote_expiry',
          title: `${quote.isProposal ? 'Proposal' : 'Quote'} expires ${composeDay(quote.validUntil, timeZone)}`,
          detail: `Lets them know ${quote.number} is valid until then`,
          brief: `Let the customer know ${what} ${quote.number} is valid until ${composeDay(quote.validUntil, timeZone)}, and that after that the price may need to be checked again.`,
          referenceIds: [`quote:${quote.id}`],
          attachmentIds: pdf,
        })
      }
    }

    const upcoming = job.visits
      .filter((visit) => LIVE_VISIT.has(visit.status) && visit.startAt > now)
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
    const next = upcoming[0]
    if (next && daysBetween(now, next.startAt) <= BOOKING_AHEAD_DAYS) {
      const when = `${composeDay(next.startAt, timeZone)} at ${composeTime(next.startAt, timeZone)}`
      const who = next.crew.length ? next.crew.join(' and ') : null
      const confirm = next.status === 'PROPOSED'
      add({
        key: `${confirm ? 'booking_confirm' : 'booking_reminder'}:${next.id}`,
        kind: confirm ? 'booking_confirm' : 'booking_reminder',
        title: confirm ? 'Confirm the booking' : 'Booking reminder',
        detail: `${who ? `${who}, ` : ''}${when}`,
        // The brief never names the crew: the customer hears "our technician", because a booking can be moved.
        brief: confirm
          ? `Offer the booking on ${when} with one of our technicians and ask the customer to confirm it suits. Do not say it is confirmed.`
          : `Remind the customer of the confirmed booking on ${when} with our technician. Mention access and pets only if the job's notes say something about them.`,
        referenceIds: [`booking:${next.id}`],
        attachmentIds: [],
      })
    }

    const nothingBooked = upcoming.length === 0
    if (nothingBooked && (job.stage === 'WORK_ORDER' || job.stage === 'QUOTE')) {
      const slot = job.freeSlot
      const quoted = job.quotes.some((quote) => quote.sentAt !== null)
      const visit =
        job.stage === 'WORK_ORDER' ? 'the work' : quoted ? 'the work, if they would like to go ahead' : 'an inspection'
      add({
        key: 'offer_time',
        kind: 'offer_time',
        title: 'Offer a time',
        detail: slot
          ? `${slot.userName} is free${slot.place ? ` in ${slot.place}` : ''} ${composeDay(slot.start, timeZone)}, ${composeTime(slot.start, timeZone)}`
          : 'Asks when suits them',
        brief: slot
          ? `Offer a time with one of our technicians on ${composeDay(slot.start, timeZone)} at ${composeTime(slot.start, timeZone)} for ${visit}, and ask the customer to confirm or suggest another time. It is an offer, not a booking.`
          : `Ask the customer which days and times suit them for ${visit}.`,
        referenceIds: [],
        attachmentIds: [],
      })
    }

    for (const invoice of job.invoices) {
      if (!OWED_INVOICE.has(invoice.status) || !invoice.sentAt) continue
      const due = invoice.totalIncTax - invoice.amountPaid
      if (due <= 0) continue
      const overdue = invoice.status === 'OVERDUE' || (invoice.dueAt !== null && invoice.dueAt < now)
      const pdf = email && invoice.lastSend?.attachment ? [invoice.lastSend.attachment.id] : []
      add({
        key: `payment_reminder:${invoice.id}`,
        kind: 'payment_reminder',
        title: overdue ? 'Overdue payment reminder' : 'Payment reminder',
        detail: `${money(due)} still owing on ${invoice.number}${invoice.dueAt ? `, due ${composeDay(invoice.dueAt, timeZone)}` : ''}`,
        brief: `A ${overdue ? 'polite overdue' : 'gentle'} reminder that ${money(due)} inc GST is still owing on invoice ${invoice.number}${
          invoice.dueAt ? `, due ${composeDay(invoice.dueAt, timeZone)}` : ''
        }. Say the amount due, not the invoice total. Thank them if they have already paid.${pdf.length ? ' The invoice PDF is attached again.' : ''}`,
        referenceIds: [`invoice:${invoice.id}`],
        attachmentIds: pdf,
      })
    }

    const report = job.reports[0]
    if (report && job.stage === 'COMPLETE') {
      add({
        key: `send_report:${report.attachmentId}`,
        kind: 'send_report',
        title: 'Send the report',
        detail: email ? `Attaches ${report.name}` : 'Lets them know the report is ready',
        brief: email
          ? `Send the service report (${report.name}, attached), summarise in one or two sentences what was done, and invite questions.`
          : 'Let the customer know the work is done and the report is ready; offer to email it.',
        referenceIds: [`report:${report.attachmentId}`],
        attachmentIds: email ? [report.attachmentId] : [],
      })
    }
  }

  return {
    signal: signalOf(input),
    suggestions,
    references: job ? referencesOf(job, timeZone) : [],
  }
}

function signalOf(input: ComposeSuggestionInput): ComposeSignal | null {
  const { job, now, timeZone } = input
  if (!job) return null

  const inbound = lastInbound(job.messages)
  const owed = job.invoices.find(
    (invoice) => OWED_INVOICE.has(invoice.status) && invoice.sentAt && invoice.totalIncTax - invoice.amountPaid > 0,
  )
  const quote = job.quotes.find((row) => OPEN_QUOTE.has(row.status) && !row.accepted && row.sentAt)

  if (inbound) {
    return {
      headline: `${inbound.channel === 'SMS' ? 'SMS' : 'Email'} from ${inbound.fromName ?? 'the customer'} · ${composeDay(inbound.at, timeZone)}, not answered`,
      tracking: null,
      tone: 'waiting',
    }
  }
  if (owed) {
    const overdue = owed.status === 'OVERDUE' || (owed.dueAt !== null && owed.dueAt < now)
    const tracking = trackingOf(owed.lastSend, timeZone)
    return {
      headline: `Invoice ${owed.number} ${overdue ? 'overdue' : 'sent'} · ${money(owed.totalIncTax - owed.amountPaid)} owing`,
      tracking: tracking?.text ?? null,
      tone: overdue || tracking?.problem ? 'problem' : 'waiting',
    }
  }
  if (quote?.sentAt) {
    const lastSentAt = quote.lastSend?.sentAt ?? quote.sentAt
    const waited = daysBetween(lastSentAt, now)
    const replied = inboundSince(job.messages, lastSentAt)
    const tracking = trackingOf(quote.lastSend, timeZone)
    return {
      headline: `${quote.isProposal ? 'Proposal' : 'Quote'} sent · ${waited === 0 ? 'today' : plural(waited, 'day')}${replied ? '' : ', no reply'}`,
      tracking: tracking?.text ?? null,
      tone: tracking?.problem ? 'problem' : 'waiting',
    }
  }
  return null
}

function referencesOf(job: ComposeJobFacts, timeZone: string): ComposeReference[] {
  return [
    ...job.quotes.map((quote) => ({
      id: `quote:${quote.id}`,
      kind: 'quote' as const,
      label: `${quote.isProposal ? 'Proposal' : 'Quote'} ${quote.number}`,
      detail: `${money(quote.totalIncTax)} · ${quote.status.toLowerCase()}`,
    })),
    ...job.invoices.map((invoice) => ({
      id: `invoice:${invoice.id}`,
      kind: 'invoice' as const,
      label: `Invoice ${invoice.number}`,
      detail: `${money(invoice.totalIncTax - invoice.amountPaid)} owing · ${invoice.status.toLowerCase()}`,
    })),
    ...job.messages.slice(0, 10).map((message) => ({
      id: `${message.channel === 'SMS' ? 'sms' : 'email'}:${message.id}`,
      kind: message.channel === 'SMS' ? ('sms' as const) : ('email' as const),
      label: messageLabel(message, timeZone),
      detail: message.direction === 'INBOUND' ? `from ${message.fromName ?? 'the customer'}` : 'we sent',
    })),
    ...job.notes.slice(0, 10).map((note) => ({
      id: `note:${note.id}`,
      kind: 'note' as const,
      label: `Note ${composeDay(note.at, timeZone)}`,
      detail: note.body.slice(0, 60),
    })),
    ...job.visits.slice(0, 6).map((visit) => ({
      id: `booking:${visit.id}`,
      kind: 'booking' as const,
      label: `Booking ${composeDay(visit.startAt, timeZone)}`,
      detail: visit.crew.join(', ') || null,
    })),
    ...job.reports.slice(0, 3).map((report) => ({
      id: `report:${report.attachmentId}`,
      kind: 'report' as const,
      label: report.name,
      detail: `report ${composeDay(report.at, timeZone)}`,
    })),
    ...job.pastJobs.slice(0, 6).map((past) => ({
      id: `job:${past.id}`,
      kind: 'job' as const,
      label: past.label,
      detail: `earlier job at this property`,
    })),
  ]
}

/** `kind:id`, split. A malformed one reads as null rather than throwing. */
export function parseReferenceId(value: string): { kind: ComposeReferenceKind; id: string } | null {
  const at = value.indexOf(':')
  if (at <= 0) return null
  const kind = value.slice(0, at) as ComposeReferenceKind
  const id = value.slice(at + 1)
  const kinds: readonly ComposeReferenceKind[] = ['quote', 'invoice', 'email', 'sms', 'note', 'booking', 'job', 'report']
  return kinds.includes(kind) && id ? { kind, id } : null
}
